import React, { useCallback } from 'react';

import { EventCardProps } from 'features/events';
import ConditionalLink from 'components/conditionalLink';
import styled from 'styled-components';
import { Flex } from 'components';
import { Icon } from 'components/icon';
import { createUrl } from 'app/utils';
import formatDateFromUtcToTimezone from 'app/utils/formatDateFromUtcToTimezone';

const Siding = styled(Flex)`
  background: ${({ theme }) => theme.colors.black80};
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  z-index: 20;
`;

const BackgroundOpacity = styled.div`
  border-radius: 4px;
  background: linear-gradient(0deg, ${({ theme }) => theme.colors.primary_400}, ${({ theme }) => theme.colors.primary_400}),
    linear-gradient(90deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.1) 100%), url(melver-raony.png);
  background-blend-mode: multiply, normal, normal;
  opacity: 0.5;
  display: none;
  position: absolute;
`;

const Content = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: space-between;
  z-index: 10;
`;

const Head = styled.div`
  display: flex;
  justify-content: space-between;
`;

const DateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem;
`;

const Day = styled.div`
  font-size: 3.75rem;
  font-weight: 100;
  line-height: 1;
`;

const Month = styled.div`
  font-size: 0.875rem;
  line-height: 1;
  text-transform: uppercase;
  font-weight: 400;
  margin-top: 0.25rem;
`;

const TimeWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 1rem;
`;

const Time = styled.div`
  font-size: 1rem;
  line-height: 1;
  font-weight: 700;
  padding: 6px 8px;
  border-radius: 4px;
  text-transform: uppercase;
`;

const Foot = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem;
`;

const Hat = styled.div`
  font-size: 0.75rem;
  line-height: 0.875rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
`;

const Name = styled.div`
  font-size: 1.125rem;
  line-height: 1.313rem;
  font-weight: 700;
`;

const TeacherName = styled.div`
  font-size: 1rem;
  line-height: 1.125rem;
  font-weight: 500;
  margin-top: 0.5rem;
`;

const EventButton = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  position: relative;
  z-index: 10;

  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
`;

const EventButtonLabel = styled.div`
  font-size: 1rem;
  line-height: 1.125em;
  font-weight: 700;
  text-transform: uppercase;
`;

const EventButtonIcon = styled(Icon)`
  width: 24px;
`;

const EventFloatButton = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.85rem 1rem;
  position: relative;
  z-index: 10;
  border-radius: 4px;
`;

const EventFloatButtonLabel = styled.div`
  font-size: 1rem;
  line-height: 1.125em;
  font-weight: 700;
  text-transform: uppercase;
`;

const EventFloatButtonIcon = styled(Icon)`
  width: 24px;
`;

const Wrapper = styled(ConditionalLink)`
  display: flex;
  flex-direction: column;

  width: ${(props: any) => (props['data-size'] === 'default' ? '280px' : props['data-size'] === 'small' ? '240px' : '320px')};
  height: ${(props: any) => (props['data-size'] === 'large' ? '395px' : '340px')};

  position: relative;
  z-index: 5;

  background-color: ${(props: any) => (props['data-image'] ? props.theme.colors.gray_100 : 'white')};

  cursor: ${(props: any) => (props['data-disabled'] ? 'inherit' : 'pointer')};

  ${(props: any) => {
    if (props['data-image']) {
      return `
        background: linear-gradient(180deg, rgba(30, 29, 31, 0.4) 12.65%, rgba(30, 29, 31, 0.1) 32.6%, rgba(30, 29, 31, 0.9) 64.41%), url(${props['data-image']});
        background-size: cover;
        background-position: top center;
      `;
    }

    return `
      border: 1px solid ${props.theme.colors.gray_100};
    `;
  }}

  ${(props: any) => {
    if (props['data-confirmation']) {
      return `
        border-radius: 4px;

        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        
        ${EventButton} {
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }

        ${Siding} {
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }

        ${BackgroundOpacity} {
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }
      `;
    }

    if (props['data-confirmationpage']) {
      return `
        border-radius: 4px;

        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        
        ${EventButton} {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }

        ${Siding} {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }

        ${BackgroundOpacity} {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }
      `;
    }

    return `
      border-radius: 4px;
    `;
  }};

  ${Day},
  ${Month} {
    color: ${(props: any) => (props['data-image'] ? props.theme.colors.white : props.theme.colors.black50)};
  }

  ${Time} {
    background: ${({ theme }) => theme.colors.primary_600};
    color: ${({ theme }) => theme.colors.primary_contrast};

    &.event {
      background: ${({ theme }) => theme.colors.alert_400};
      color: ${({ theme }) => theme.colors.white};
    }
  }

  ${Hat} {
    color: ${({ theme }) => theme.colors.primary_400};
  }

  ${Name} {
    color: ${(props: any) => (props['data-image'] ? props.theme.colors.white : props.theme.colors.dark_350)};
  }

  ${TeacherName} {
    color: ${(props: any) => (props['data-image'] ? props.theme.colors.white50 : props.theme.colors.black30)};
  }

  ${BackgroundOpacity} {
    width: 100%;
    height: ${(props: any) => (props['data-size'] === 'large' ? '395px' : '340px')};
  }

  ${EventButton} {
    background: ${(props: any) => (props['data-image'] ? props.theme.colors.black30 : props.theme.colors.gray_100)};
  }

  ${EventButtonLabel} {
    color: ${({ theme }) => theme.colors.primary_400};

    &.notEnrolled {
      color: ${({ theme }) => theme.colors.gray_500};
    }
  }

  ${EventButtonIcon} {
    color: ${({ theme }) => theme.colors.primary_400};
    &.notEnrolled {
      color: ${({ theme }) => theme.colors.gray_500};
    }
  }

  ${EventFloatButton} {
    background: ${(props: any) =>
      props['data-disabled']
        ? props['data-image']
          ? props.theme.colors.black30
          : props.theme.colors.black50
        : props['data-image']
        ? props.theme.colors.primary_400
        : props.theme.colors.black50};
  }

  ${EventFloatButtonLabel} {
    color: ${({ theme }) => theme.colors.primary_contrast};
  }

  ${EventFloatButtonIcon} {
    color: ${({ theme }) => theme.colors.primary_contrast};
  }

  ${(props: any) => {
    if (!props['data-disabled']) {
      return `
        &:hover {
          box-shadow: 0px 4px 8px ${props.theme.colors.black20};
          border-color: ${props.theme.colors.primary_400};

          ${BackgroundOpacity} {
            display: block;
          }

          ${EventButton} {
            background: ${props.theme.colors.primary_400};
          }

          ${EventButtonLabel} {
            color: ${props.theme.colors.primary_contrast};
          }

          ${EventButtonIcon} {
            color: ${props.theme.colors.primary_contrast};
          }

          ${EventFloatButton} {
            background: ${props.theme.colors.primary_200};
          }
        }
      `;
    }
  }}
`;

const Event = (eventCard: EventCardProps) => {
  const {
    uuid,
    name,
    hat,
    hatName,
    startDate,
    enrolled,
    buttonIcon,
    size,
    image,
    buttonLabel,
    slug,
    allowEnrollemnts,
    commentContentUuid,
    disabled,
    theme = 'light',
  } = eventCard;

  //   const { setOpenEventConfirmation } = useEvents();

  let formatedDate = startDate ? formatDateFromUtcToTimezone(startDate) : null;

  let finalButtonIcon = buttonIcon;

  if (disabled)
    return (
      <Wrapper type={'nolink'} to={''} component={Flex} onClick={() => {}} data-size={size} data-image={image} data-disabled={true}>
        <Content>
          <Head />

          <Foot>
            {!!hat && <Hat>{hat}</Hat>}

            <Name>{name}</Name>

            {!!hatName && <TeacherName>{hatName}</TeacherName>}
          </Foot>
        </Content>

        <EventButton>
          <EventButtonLabel className="notEnrolled">{'Somente para alunos'}</EventButtonLabel>
          <EventButtonIcon className="notEnrolled" icon="lockOutline" />
        </EventButton>
      </Wrapper>
    );

  return (
    <Wrapper
      type={'internalNav'}
      to={createUrl('events/details', [slug, uuid, commentContentUuid])}
      target={'_parent'}
      component={Flex}
      data-size={size}
      data-image={image}
    >
      <Content>
        <Head>
          <DateWrapper>
            {!!formatedDate && (
              <>
                <Day>{formatedDate.format('DD')}</Day>
                <Month>{formatedDate.format('MMMM')}</Month>
              </>
            )}
          </DateWrapper>

          {/* <TimeWrapper>
            {!!formatedDate && enrolled && (
              <Time className={eventType === EventTypeEnum.Online ? 'event' : ''}>
                {eventType === EventTypeEnum.Online ? 'Online' : 'Presencial'}
              </Time>
            )}
          </TimeWrapper> */}
        </Head>

        <Foot>
          {!!hat && <Hat>{hat}</Hat>}

          <Name>{name}</Name>
        </Foot>
      </Content>

      <EventButton>
        <EventButtonLabel>{!disabled ? buttonLabel : ''} </EventButtonLabel>
        <EventButtonIcon icon={!disabled ? finalButtonIcon : 'lockOutline'} />
      </EventButton>
    </Wrapper>
  );
};

export default Event;
